import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * Public unsubscribe page. Recipients click the link in the broadcast footer,
 * we confirm (one-click), then show a success screen. No login required.
 */
export default function Unsubscribe() {
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [state, setState] = useState({ loading: true, err: null, done: false, email: "" });

  useEffect(() => {
    if (!token) { setState({ loading: false, err: "No unsubscribe token supplied.", done: false, email: "" }); return; }
    api.get(`/marketing/unsubscribe/${token}`)
      .then((r) => setState({ loading: false, err: null, done: !!r.data?.already, email: r.data?.email || "" }))
      .catch((e) => setState({ loading: false, err: formatApiError(e.response?.data?.detail) || e.message, done: false, email: "" }));
  }, [token]);

  const confirm = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      await api.post(`/marketing/unsubscribe/${token}`);
      setState((s) => ({ ...s, loading: false, done: true }));
    } catch (e) {
      setState((s) => ({ ...s, loading: false, err: formatApiError(e.response?.data?.detail) || e.message }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain px-4 py-10" data-testid="unsubscribe-page">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-secondary" /></div>
          <span className="font-display font-semibold text-lg">Treasurescare</span>
        </Link>
        {state.loading && <div className="text-sm text-muted-foreground">Checking…</div>}
        {!state.loading && state.err && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="text-destructive text-sm">{state.err}</div>
          </div>
        )}
        {!state.loading && !state.err && !state.done && (
          <div className="bg-card border border-border rounded-2xl p-6 text-left">
            <div className="overline text-secondary">Unsubscribe</div>
            <h1 className="mt-1 font-display text-2xl font-light">Sorry to see you go.</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We'll stop sending marketing emails to {state.email ? <strong className="text-foreground">{state.email}</strong> : "this address"} immediately.
              You'll still receive transactional messages (claim updates, receipts) so your protection keeps working.
            </p>
            <Button className="mt-4 w-full" onClick={confirm} data-testid="unsubscribe-confirm-btn">Confirm unsubscribe</Button>
          </div>
        )}
        {!state.loading && state.done && (
          <div className="bg-card border border-border rounded-2xl p-6" data-testid="unsubscribe-done">
            <CheckCircle2 className="w-10 h-10 mx-auto text-primary" />
            <h1 className="mt-3 font-display text-2xl font-light">You're unsubscribed.</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We won't send you marketing emails anymore. Change your mind? Just email <a className="text-primary" href="mailto:hello@treasurescare.com">hello@treasurescare.com</a>.
            </p>
            <Link to="/" className="inline-block mt-4">
              <Button variant="outline">Back to Treasurescare</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
